import { find } from 'lodash';
/**
   * @ngdoc directive
   * @name adjust.apps.directive:selectify
   * @scope
   * @restrict E
   *
   * @description
   * Select directive which replaces native select
   *
   * @param {string} name name of the input
   * @param {string=label} labelKey label key name
   * @param {string=value} valueKey value key name
   * @param {string} placeholder input placeholder
   * @param {string} checkIfEmpty check if provided parameter is empty
   * @param {Object} options list of options
   * @param {Object} ngModel selected value
   * @param {Function} onSelect on select callback
   *
   */
function selectify(Helper) {
  'ngInject';
  return {
    restrict: 'E',
    template: require('./selectify.html'),
    scope: {},
    controller: 'SelectifyController',
    controllerAs: 'vm',
    require: ['selectify', '^ngModel'],
    bindToController: {
      name: '@',
      labelKey: '@',
      valueKey: '@',
      doTranslate: '@',
      placeholder: '@',
      checkIfEmpty: '@',
      options: '=?',
      ngModel: '=?',
      onSelect: '&?'
    },
    compile: function(element, attrs) {

      attrs.labelKey = attrs.labelKey || 'label';
      attrs.valueKey = attrs.valueKey || 'value';
      attrs.doTranslate = attrs.doTranslate || false;

      return Helper.linkFunction;
    }
  };
}

/**
   * @ngdoc controller
   * @name adjust.apps.controller:SelectifyController
   *
   * @description
   * All the functionality of the {@link adjust.apps.directive:select select} directive
   *
   */
function SelectifyController($scope, $element, $document, Helper, gettextCatalog) {
  'ngInject';
  var vm = this;

  var _modelController;
  var _dropdown = $element[0].querySelector('.selectify-dropdown ul');
  var _input = $element[0].querySelector('.selectify-input');

  vm.show = false;

  vm.init = init;
  vm.onFocus = onFocus;
  vm.deactivate = deactivate;
  vm.select = select;
  vm.isEmpty = isEmpty;
  vm.isSelected = isSelected;

  Helper.ready(_prepare);
  $scope.$watch('vm.ngModel', _handleValueChange);
  $scope.$watchCollection('vm.options', _handleValueChange);
  $scope.$on('$destroy', _unbind);

  /**
   * @ngdoc method
   * @name init
   * @methodOf adjust.apps.controller:SelectifyController
   *
   * @description
   * Register ngModel controller
   *
   * @param ctrl
   */
  function init(ctrl) {
    _modelController = ctrl;
  }

  /**
   * @ngdoc method
   * @name _handleValueChange
   * @methodOf adjust.apps.controller:SelectifyController
   *
   * @description
   * On external ngModel change do the view prepare
   *
   * @param {string|number} n old value
   * @param {string|number} o new value
   * @private
   */
  function _handleValueChange(n, o) {
    if (n !== o) {
      _prepare();
    }
  }

  /**
   * @ngdoc method
   * @name _prepare
   * @methodOf adjust.apps.controller:SelectifyController
   *
   * @description
   * Prepare view values
   *
   * @private
   */
  function _prepare() {

    var item = find(vm.options, function(option) {
      return option[vm.valueKey] === vm.ngModel;
    });

    if (!item) {
      return;
    }

    vm.ngModelView = vm.doTranslate ? gettextCatalog.getString(item[vm.labelKey]) : item[vm.labelKey];
  }

  /**
   * @ngdoc method
   * @name _keyHandler
   * @methodOf adjust.apps.controller:SelectifyController
   *
   * @description
   * Handle enter, up and down key arrow events
   *
   * @param {Object} e keydown event
   * @private
   */
  function _keyHandler(e) {

    var min = 0;
    var max = vm.options.length - 1;

    if (e.keyCode === 13) { // enter

      _input.blur();

      vm.select(vm.options[vm.focus]);

      $scope.$digest();

      return;
    }

    if (e.keyCode !== 40 && e.keyCode !== 38) { return; }

    if (e.keyCode === 40) { // key arrow down
      vm.focus = (vm.focus >= max) ? min : (vm.focus + 1);
    }

    if (e.keyCode === 38) { // key arrow up
      vm.focus = (vm.focus <= min) ? max : (vm.focus - 1);
    }

    $scope.$digest();

    var activeItem = _dropdown.querySelector('.active');
    var dropdownHeight = _dropdown.offsetHeight;
    var currentOffset = activeItem ? activeItem.offsetHeight * vm.focus : 0;

    if (currentOffset >= dropdownHeight) {
      _dropdown.scrollTop = currentOffset;
    } else if (currentOffset <= _dropdown.scrollTop) {
      _dropdown.scrollTop = 0;
    }

  }

  /**
   * @ngdoc method
   * @name _checkOutsideClick
   * @methodOf adjust.apps.controller:SelectifyController
   *
   * @description
   * Checks for the outside clicks to blur the input and dropdown
   *
   * @param {Object} evt click event
   *
   * @private
   */
  function _checkOutsideClick(evt) {

    if (!$element[0].contains(evt.target)) {
      deactivate();
      $scope.$apply();
    }

  }

  /**
   * @ngdoc method
   * @name _unbind
   * @methodOf adjust.apps.controller:SelectifyController
   *
   * @description
   * Unbind keydown event
   *
   * @private
   */
  function _unbind() {

    vm.focus = 0;

    $element.off('keydown', _keyHandler);
    $document.off('mousedown', _checkOutsideClick);

  }

  /**
   * @ngdoc method
   * @name _bind
   * @methodOf adjust.apps.controller:SelectifyController
   *
   * @description
   * Bind keydown event
   *
   * @private
   */
  function _bind() {

    $element.on('keydown', _keyHandler);
    // selected value container hides on click that is why mousedown is being used
    $document.on('mousedown', _checkOutsideClick);

  }

  /**
   * @ngdoc method
   * @name onFocus
   * @methodOf adjust.apps.controller:SelectifyController
   *
   * @description
   * Show dropdown on input focus and bind events
   */
  function onFocus() {

    vm.show = true;
    vm.focus = 0;

    _bind();

  }

  /**
   * @ngdoc method
   * @name deactivate
   * @methodOf adjust.apps.controller:SelectifyController
   *
   * @description
   * Hide dropdown on input blur and unbind events
   */
  function deactivate() {

    _input.blur();

    vm.show = false;

    _unbind();

  }

  /**
   * @ngdoc method
   * @name select
   * @methodOf adjust.apps.controller:SelectifyController
   *
   * @description
   * Select value from the list
   *
   * @param {Object} item selected value from the list
   */
  function select(item) {

    if (!item || isEmpty(item) || isSelected(item)) { return; }

    vm.show = false;
    vm.ngModel = item[vm.valueKey];
    _modelController.$setViewValue(item[vm.valueKey]);

    _prepare();

    if (angular.isDefined(vm.onSelect)) {
      vm.onSelect({value: item});
    }

    deactivate();

  }

  /**
   * @ngdoc method
   * @name isEmpty
   * @methodOf adjust.apps.controller:SelectifyController
   *
   * @description
   * Check if particular parameter needs to be check and if that parameter is empty
   *
   * @param {Object} item item to check
   * @returns {boolean} is parameter empty
   */
  function isEmpty(item) {

    return item && vm.checkIfEmpty && !item[vm.checkIfEmpty];

  }

  /**
   * @ngdoc method
   * @name isSelected
   * @methodOf adjust.apps.controller:SelectifyController
   *
   * @description
   * Check if item is already selected
   *
   * @param {Object} item item to check
   * @returns {boolean} is item selected
   */
  function isSelected(item) {

    return item[vm.valueKey] === vm.ngModel;

  }
}

export { SelectifyController, selectify };
